import { createFileRoute } from "@tanstack/react-router";
import Link from "~/components/Link";
import { StudentGoal } from "~/app/(new)/[teamId]/(with-student-nav)/students/[studentId]/StudentGoal";
import { trpc } from "~/utils/trpc.client";
import { useParams, useRouter, useSearchParams } from "~/components/navigation";
import { PageLoading } from "~/components/PageLoading";
import { Dispatch, SetStateAction, useState } from "react";
import { Dropdown } from "~/components/Dropdown";
import { Button } from "~/components/Button";
import { GoalEssence, GoalMilestoneEssence } from "~/db";
import { errHandler } from "~/utils/clientLogger";
import toast from "react-hot-toast";
import { produce } from "immer";
import { GoalType } from "~/sharedTypes";
import { WithStudentNav } from "~/components/WithStudentNav";

export const Route = createFileRoute(
  "/_authed/$teamId/students/$studentId/goals/",
)({
  component: GoalsPage,
});

interface SuggestedGoal {
  goal: GoalEssence;
  llmData: string;
  subgoals: GoalEssence[];
  milestones: GoalMilestoneEssence[];
}

const SuggestedGoalEditor = (props: {
  studentId: string;
  goal: SuggestedGoal;
  index: number;
  setSuggestedGoals: Dispatch<SetStateAction<SuggestedGoal[]>>;
}) => {
  const { studentId, goal, index, setSuggestedGoals } = props;
  const { teamId } = useParams<{ teamId: string }>();
  const createGoal = trpc.goals.createGoal.useMutation();
  const createGoalMilestone = trpc.goals.createGoalMilestone.useMutation();

  const extractSubgoalsFromGoal =
    trpc.goals.extractSubgoalsFromGoal.useMutation();
  const extractMilestonesFromGoal =
    trpc.goals.extractMilestonesFromGoal.useMutation();

  return (
    <div className="card bg-base-100 shadow" key={goal.goal.description}>
      <div className="card-body">
        <div className="flex justify-between gap-2">
          <h3 className="font-bold text-lg opacity-80">
            Pending Goal:{" "}
            <span className="capitalize">{goal.goal.goalArea}</span>
          </h3>
          <Button
            className="btn btn-sm btn-circle btn-ghost"
            onClick={() => {
              setSuggestedGoals((prev) => prev.filter((_, j) => j !== index));
            }}
          >
            <span className="ph ph-trash"></span>
          </Button>
        </div>
        <p className="text-sm text-gray-600">
          {goal.goal.description === ""
            ? "No description."
            : goal.goal.description}
        </p>

        {/* if there are subgoals, show them */}
        {goal.subgoals.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex gap-1 items-center">
              <h4 className="font-bold text-md opacity-80">
                Short Term Objectives
              </h4>
              <Button
                className="btn btn-sm btn-circle btn-ghost"
                onClick={() => {
                  setSuggestedGoals(
                    produce((draft) => {
                      if (draft[index]) {
                        draft[index].subgoals = [];
                      }
                    }),
                  );
                }}
              >
                <span className="ph ph-trash"></span>
              </Button>
            </div>
            <div className="flex flex-col gap-4">
              {goal.subgoals.map((sg) => (
                <div
                  key={sg.description}
                  className="border-l-4 pl-2 border-base-content/20"
                >
                  <div className="flex flex-col gap-2">
                    <div className="text-sm opacity-80">{sg.description}</div>
                    <div className="flex flex-wrap gap-4">
                      <div className="text-sm opacity-80">
                        Goal Type: {sg.goalType}
                      </div>
                      <div className="text-sm opacity-80">
                        Start On: {sg.startOn.toLocaleDateString()}
                      </div>
                      <div className="text-sm opacity-80">
                        Target: {sg.target}
                        {sg.denominator && goal.goal.goalType === GoalType.ratio
                          ? ` out of ${String(sg.denominator)}`
                          : ""}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* if there are milestones, show them */}
        {goal.milestones.length > 0 && (
          <div className="flex flex-col gap-2 mt-4">
            <div className="flex gap-1 items-center">
              <h4 className="font-bold text-md opacity-80">Benchmarks</h4>
              <Button
                className="btn btn-sm btn-circle btn-ghost"
                onClick={() => {
                  setSuggestedGoals(
                    produce((draft) => {
                      if (draft[index]) {
                        draft[index].milestones = [];
                      }
                    }),
                  );
                }}
              >
                <span className="ph ph-trash"></span>
              </Button>
            </div>
            <ul className="text-sm pl-4 opacity-80">
              {goal.milestones.map((milestone) => (
                <li className="list-disc" key={milestone.value}>
                  {milestone.value} at week {milestone.week}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* if there are neighter subgoals or milestones, show buttons to extract them */}
        {goal.subgoals.length === 0 && goal.milestones.length === 0 && (
          <div className="flex justify-center gap-2 w-full">
            <Button
              loading={extractSubgoalsFromGoal.isPending}
              disabled={extractMilestonesFromGoal.isPending}
              className="btn btn-sm btn-secondary"
              onClick={() => {
                extractSubgoalsFromGoal
                  .mutateAsync({
                    goal,
                    teamId,
                    studentId,
                  })
                  .then((data) => {
                    setSuggestedGoals(
                      produce((draft) => {
                        if (draft[index]) {
                          draft[index].subgoals = data.map((sg) => ({
                            ...sg,
                            startOn: goal.goal.startOn,
                            goalArea: goal.goal.goalArea,
                          }));
                        }
                      }),
                    );
                  })
                  .catch(errHandler("extracting short term objectives"));
              }}
            >
              Add Short Term Objectives
            </Button>
            <Button
              loading={extractMilestonesFromGoal.isPending}
              disabled={extractSubgoalsFromGoal.isPending}
              className="btn btn-sm btn-secondary"
              onClick={() => {
                extractMilestonesFromGoal
                  .mutateAsync({
                    goal,
                    teamId,
                    studentId,
                  })
                  .then((data) => {
                    setSuggestedGoals(
                      produce((draft) => {
                        if (draft[index]) {
                          draft[index].milestones = data;
                        }
                      }),
                    );
                  })
                  .catch(errHandler("extracting benchmarks"));
              }}
            >
              Add Benchmarks
            </Button>
          </div>
        )}
        <div className="w-full flex justify-end gap-2 items-baseline mt-4">
          <div className="text-sm text-gray-600">
            (Hint: You can edit a goal after adding it)
          </div>
          <div>
            <Button
              className="btn btn-primary btn-sm"
              onClick={() => {
                createGoal
                  .mutateAsync({
                    studentId: studentId,
                    goalArea: goal.goal.goalArea,
                    description: goal.goal.description,
                    goalType: goal.goal.goalType,
                    startOn: goal.goal.startOn,
                    target: goal.goal.target,
                    isNegative: goal.goal.isNegative,
                    denominator: goal.goal.denominator ?? undefined,
                  })
                  .then((newGoal) => {
                    // then create the milestones
                    return Promise.all(
                      goal.milestones.map((milestone) =>
                        createGoalMilestone.mutateAsync({
                          goalId: newGoal.id,
                          value: milestone.value,
                          week: milestone.week,
                        }),
                      ),
                    ).then(() => newGoal);
                  })
                  .then((newGoal) => {
                    // then create the subgoals
                    return Promise.all(
                      goal.subgoals.map((sg) =>
                        createGoal.mutateAsync({
                          studentId: studentId,
                          goalArea: sg.goalArea,
                          description: sg.description,
                          goalType: sg.goalType,
                          startOn: sg.startOn,
                          target: sg.target,
                          isNegative: sg.isNegative,
                          denominator: sg.denominator ?? undefined,
                          parentGoalId: newGoal.id,
                        }),
                      ),
                    );
                  })
                  .then(() => {
                    setSuggestedGoals((prev) => {
                      return prev.filter((_, j) => index !== j);
                    });
                    toast.success("Goal created!");
                  })
                  .catch(errHandler("creating goal"));
              }}
            >
              Add Goal
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

function GoalsPage() {
  const params = useParams<{
    teamId: string;
    studentId: string;
  }>();

  const searchParams = useSearchParams();
  const router = useRouter();

  const student = trpc.students.getStudent.useQuery({
    id: params.studentId,
  });
  const goals = trpc.goals.getGoals.useQuery({
    studentId: params.studentId,
  });
  const measurements = trpc.goals.getGoalMeasurementsForStudent.useQuery({
    studentId: params.studentId,
  });
  const milestones = trpc.goals.getGoalMilestonesForStudent.useQuery({
    studentId: params.studentId,
  });
  const reports = trpc.reports.getReportsByStudent.useQuery({
    studentId: params.studentId,
  });
  const files = trpc.files.getFilesByStudent.useQuery({
    studentId: params.studentId,
  });

  const logStudentView = trpc.students.logStudentView.useMutation();
  useState(() => {
    logStudentView.mutate({
      studentId: params.studentId,
    });
  });

  const [suggestedGoals, setSuggestedGoals] = useState<SuggestedGoal[]>([]);

  const extractGoalsFromReportOrFile =
    trpc.goals.extractGoalsFromReportOrFile.useMutation();

  const createGoal = trpc.goals.createGoal.useMutation();

  if (!goals.data || !measurements.data || !milestones.data || !student.data) {
    return (
      <WithStudentNav>
        <div className="m-auto flex w-full max-w-[800px] flex-col items-start gap-4 p-4">
          <PageLoading />
        </div>
      </WithStudentNav>
    );
  }

  return (
    <WithStudentNav>
      <div className="m-auto flex w-full max-w-[800px] flex-col items-start gap-4 p-4">
        <div className="flex flex-row w-full">
          <div className="breadcrumbs text-sm overflow-visible">
            <ul>
              <li>
                <Link href={`/${params.teamId}`}>Team Home</Link>
              </li>
              <li>
                <Link href={`/${params.teamId}/students/${params.studentId}`}>
                  {student.data.name}
                </Link>
              </li>
              <li>
                <span>Goals</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex justify-between w-full">
          <Link href={`/${params.teamId}/students/${params.studentId}/goals`}>
            <h2 className="text-xl font-bold">Goals</h2>
          </Link>
          <div className="flex gap-4 flex-row-reverse">
            {goals.data.length > 0 && (
              <Link
                href={`/${params.teamId}/students/${params.studentId}/goals/track`}
                className="btn btn-primary btn-sm"
              >
                Track Progress
              </Link>
            )}

            <Dropdown
              initiallyOpen={searchParams.get("addGoal") === "1"}
              btn={
                <Button
                  loading={extractGoalsFromReportOrFile.isPending}
                  className={`btn btn-sm ${
                    goals.data.length === 0 ? "btn-primary" : "btn-secondary"
                  }`}
                >
                  Add Goals
                </Button>
              }
            >
              <ul>
                <li>
                  <Button
                    onClick={() => {
                      // create a barebones goal and then go to edit it
                      createGoal
                        .mutateAsync({
                          studentId: params.studentId,
                          description: "",
                          goalType: GoalType.percent,
                          goalArea: "",
                          startOn: new Date(),
                          target: 0,
                        })
                        .then((newGoal) => {
                          router.push(
                            `/${params.teamId}/students/${params.studentId}/goals/${newGoal.id}/edit`,
                          );
                        })
                        .catch(errHandler("creating goal"));
                    }}
                  >
                    Create Goal Manually
                  </Button>
                </li>
                <div className="divider h-0"></div>
                <div className="font-bold opacity-70">
                  Import Goals from Report
                </div>
                {reports.data?.map((report) => (
                  <li key={report.id}>
                    <Button
                      onClick={() => {
                        extractGoalsFromReportOrFile
                          .mutateAsync({
                            reportId: report.id,
                            teamId: params.teamId,
                            studentId: params.studentId,
                          })
                          .then((data) => {
                            setSuggestedGoals((prev) => [
                              ...prev,
                              ...data.map((goal) => {
                                const { additionalDetails, ...goalEssence } =
                                  goal;
                                return {
                                  goal: goalEssence,
                                  llmData: JSON.stringify(additionalDetails),
                                  subgoals: [],
                                  milestones: [],
                                };
                              }),
                            ]);
                            // scroll to the bottom of the page after adding goals
                            setTimeout(() => {
                              const suggestedGoalsElement =
                                document.getElementById("suggested-goals");
                              if (suggestedGoalsElement) {
                                suggestedGoalsElement.scrollIntoView({
                                  behavior: "smooth",
                                });
                              }
                            }, 100);
                          })
                          .catch(errHandler("import goals from report"));
                      }}
                    >
                      {report.name}
                    </Button>
                  </li>
                ))}
                {reports.data?.length === 0 && (
                  <div className="text-sm opacity-70 p-4 italic">
                    No reports
                  </div>
                )}
                <div className="divider h-0"></div>
                <div className="font-bold opacity-70">
                  Import Goals from File
                </div>
                {files.data?.map((file) => (
                  <li key={file.id}>
                    <Button
                      onClick={() => {
                        extractGoalsFromReportOrFile
                          .mutateAsync({
                            fileId: file.id,
                            teamId: params.teamId,
                            studentId: params.studentId,
                          })
                          .then((data) => {
                            setSuggestedGoals((prev) => [
                              ...prev,
                              ...data.map((goal) => ({
                                goal: goal,
                                llmData: JSON.stringify(goal.additionalDetails),
                                subgoals: [],
                                milestones: [],
                              })),
                            ]);
                            // scroll to the bottom of the page after adding goals
                            setTimeout(() => {
                              const suggestedGoalsElement =
                                document.getElementById("suggested-goals");
                              if (suggestedGoalsElement) {
                                suggestedGoalsElement.scrollIntoView({
                                  behavior: "smooth",
                                });
                              }
                            }, 100);
                          })
                          .catch(errHandler("importing goals from file"));
                      }}
                    >
                      {file.name}
                    </Button>
                  </li>
                ))}
                {files.data?.length === 0 && (
                  <div className="text-sm opacity-70 p-4 italic">No files</div>
                )}
              </ul>
            </Dropdown>
          </div>
        </div>
        <div className="flex flex-col gap-4 w-full">
          {goals.data
            .filter((g) => g.parentGoalId == null)
            .map((goal) => (
              <div className="card bg-base-100 shadow" key={goal.id}>
                <div className="card-body">
                  <StudentGoal
                    goal={goal}
                    // don't show any subgoals on this page
                    goals={goals.data}
                    measurements={measurements.data}
                    milestones={milestones.data}
                  />
                </div>
              </div>
            ))}
          <div id="suggested-goals"></div>
          {suggestedGoals.map((goal, i) => (
            <SuggestedGoalEditor
              key={i}
              studentId={params.studentId}
              goal={goal}
              index={i}
              setSuggestedGoals={setSuggestedGoals}
            />
          ))}
        </div>
        {
          // If no goals, show an empty state that's full width and 5rem tall that says "No goals"
          goals.data.length === 0 && (
            <div className="flex h-16 items-center justify-center text-center text-sm italic opacity-70 w-full">
              No goals
            </div>
          )
        }
      </div>
    </WithStudentNav>
  );
}
